// src/components/StrategicPointModal.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axios from "../axiosConfig";
import "../styles/StrategicPointModal.css";
import {
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaGlobeEurope,
  FaCity,
  FaFlag,
  FaLink,
  FaCrosshairs,
  FaTrashAlt,
  FaMagic,
  FaChevronDown,
  FaExchangeAlt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const blueSelectStyles = {
  control: (base) => ({
    ...base,
    width: "100%",
    minWidth: 0,
    minHeight: 39,
    height: 39,
    backgroundColor: "transparent",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    cursor: "pointer",
    alignItems: "center",
    display: "flex",
    "&:hover": {
      border: "none",
      boxShadow: "none",
      background: "transparent",
    },
  }),

  valueContainer: (base) => ({
    ...base,
    height: 37,
    minHeight: 37,
    padding: "0 44px 0 42px",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  }),

  input: (base) => ({
    ...base,
    color: "#e8f0ff",
    fontSize: 13,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    lineHeight: "normal",
  }),

  inputContainer: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    height: "100%",
  }),

  singleValue: (base) => ({
    ...base,
    color: "#e8f0ff",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: "18px",
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#8c94a6",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: "18px",
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),

  indicatorsContainer: (base) => ({
    ...base,
    height: 39,
    display: "flex",
    alignItems: "center",
    paddingRight: 8,
  }),

  indicatorSeparator: () => ({ display: "none" }),

  clearIndicator: (base) => ({
    ...base,
    padding: 5,
    color: "#8c94a6",
    cursor: "pointer",
    "&:hover": { color: "#ff6b7f" },
  }),

  dropdownIndicator: (base) => ({
    ...base,
    padding: 5,
    color: "#a8b2c6",
    "&:hover": { color: "#ffffff" },
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 1000000,
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: "#15181d",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 18px 45px rgba(0, 0, 0, 0.48)",
    zIndex: 1000000,
  }),

  menuList: (base) => ({
    ...base,
    padding: 4,
  }),

  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: state.isSelected
      ? "rgba(1, 157, 238, 0.22)"
      : state.isFocused
      ? "rgba(255, 255, 255, 0.07)"
      : "transparent",
    color: "#e8f0ff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  }),
};

const formatDateToInputText = (value) => {
  if (!value) return "";

  const s = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [year, month, day] = s.split("-");
    return `${day}/${month}/${year}`;
  }

  return s;
};

const formatDateTextToIso = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);

  if (digits.length !== 8) return "";

  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  return `${year}-${month}-${day}`;
};

const maskDateInput = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const maskHourInput = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 4);

  if (digits.length <= 2) return digits;

  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const normalizeHourInput = (value) => {
  const masked = maskHourInput(value);
  return masked.length === 5 ? masked : "";
};

const getPlaceName = (place) =>
  place?.placeName ||
  place?.place_name ||
  place?.name ||
  place?.label ||
  place?.location ||
  "";

const getPlacePostalCode = (place) =>
  place?.postalCode ||
  place?.postal_code ||
  place?.postcode ||
  place?.postCode ||
  "";

const getPlaceGMapsLink = (place) =>
  place?.gMapsLink ||
  place?.googleMapsLink ||
  place?.google_maps_link ||
  place?.mapsLink ||
  place?.maps_link ||
  "";

const getPlaceContactPhone = (place) =>
  place?.contactPhone ||
  place?.contact_phone ||
  place?.phone ||
  place?.phoneNumber ||
  place?.phone_number ||
  "";

function StrategicPointModal({
  isOpen,
  onClose,
  onSubmit,
  defaultDate,
  mode = "create",
  initialData = null,
}) {
  const { t } = useTranslation("strategic_point_modal");

  const [allPlaces, setAllPlaces] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    axios
      .get("/places")
      .then((res) => {
        const payload = res.data;

        const places = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.places)
          ? payload.places
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        setAllPlaces(places);
      })
      .catch(() => setAllPlaces([]));
  }, [isOpen]);

  const getPlaceLabel = (place) =>
    String(getPlaceName(place)).trim();

  const placeOptions = useMemo(
    () =>
      (allPlaces || [])
        .map((place) => {
          const label = getPlaceLabel(place);

          return {
            value: place.id,
            label,
            fullData: place,
          };
        })
        .filter((option) => option.label),
    [allPlaces]
  );

  const pointPurposeOptions = useMemo(
    () => [
      {
        value: "GENERAL",
        label: t("pointPurpose.general", "Ponto estratégico normal"),
      },
      {
        value: "FUEL",
        label: t("pointPurpose.fuel", "Abastecimento"),
      },
    ],
    [t]
  );

  const [form, setForm] = useState({
    location: "",
    strategicPointPurpose: "GENERAL",
    hasTrailerSwap: false,
    date: defaultDate || "",
    hour: "",
    address: "",
    postalCode: "",
    city: "",
    country: "",
    gMapsLink: "",
    coordinates: "",
    contactPhone: "",
    placeId: null,
  });

  const selectedPlaceValue = useMemo(() => {
    const currentLocation = String(form.location || "").trim();

    if (form.placeId != null) {
      const found = placeOptions.find(
        (option) => String(option.value) === String(form.placeId)
      );

      if (found) return found;
    }

    if (currentLocation) {
      return {
        value: form.placeId ?? currentLocation,
        label: currentLocation,
      };
    }

    return null;
  }, [form.placeId, form.location, placeOptions]);

  const selectedPointPurposeValue = useMemo(
    () =>
      pointPurposeOptions.find(
        (option) => option.value === (form.strategicPointPurpose || "GENERAL")
      ) || pointPurposeOptions[0],
    [form.strategicPointPurpose, pointPurposeOptions]
  );

  const [dateInput, setDateInput] = useState(formatDateToInputText(defaultDate || ""));
  const [hourInput, setHourInput] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && initialData) {
      const normDate = (d) => {
        if (!d) return "";
        const s = String(d);
        return s.includes("T") ? s.split("T")[0] : s;
      };

      const normHour = (h) => {
        if (!h) return "";
        const s = String(h);
        return s.length >= 5 ? s.slice(0, 5) : s;
      };

      const nextDate = normDate(initialData.date || defaultDate || "");
      const nextHour = normHour(initialData.hour || "");

      setForm({
        location:
          initialData.location ||
          getPlaceName(initialData.place) ||
          initialData.placeName ||
          "",
        strategicPointPurpose:
          String(initialData.strategicPointPurpose || "GENERAL").toUpperCase() === "FUEL"
            ? "FUEL"
            : "GENERAL",
        hasTrailerSwap:
          initialData.hasTrailerSwap === true ||
          String(initialData.hasTrailerSwap || "").toLowerCase() === "true",
        date: nextDate,
        hour: nextHour,
        address: initialData.address || initialData.place?.address || "",
        city: initialData.city || initialData.place?.city || "",
        country: initialData.country || initialData.place?.country || "",
        coordinates: initialData.coordinates || initialData.place?.coordinates || "",
        postalCode: initialData.postalCode || getPlacePostalCode(initialData.place) || "",
        gMapsLink: initialData.gMapsLink || getPlaceGMapsLink(initialData.place) || "",
        contactPhone: initialData.contactPhone || getPlaceContactPhone(initialData.place) || "",
        placeId:
          initialData.placeId != null
            ? Number(initialData.placeId)
            : initialData.place?.id != null
            ? Number(initialData.place.id)
            : null,
      });

      setDateInput(formatDateToInputText(nextDate));
      setHourInput(nextHour);

      setErrors({});
      return;
    }

    setForm({
      location: "",
      strategicPointPurpose: "GENERAL",
      hasTrailerSwap: false,
      date: defaultDate || "",
      hour: "",
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",
      placeId: null,
    });

    setDateInput(formatDateToInputText(defaultDate || ""));
    setHourInput("");

    setErrors({});
  }, [isOpen, defaultDate, mode, initialData]);

  const clearError = (fieldName) => {
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;

      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    clearError(name);
  };

  const handleDateChange = (e) => {
    const masked = maskDateInput(e.target.value);
    const isoDate = formatDateTextToIso(masked);

    setDateInput(masked);

    setForm((prev) => ({
      ...prev,
      date: isoDate,
    }));

    clearError("date");
  };

  const handleHourChange = (e) => {
    const masked = maskHourInput(e.target.value);
    const normalizedHour = normalizeHourInput(masked);

    setHourInput(masked);

    setForm((prev) => ({
      ...prev,
      hour: normalizedHour,
    }));

    clearError("hour");
  };

  const forceCloseNativeDatePicker = () => {
    datePickerRef.current?.blur();

    if (
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }

    setDatePickerKey((prev) => prev + 1);
  };

  const handleNativeDateChange = (e) => {
    const isoDate = e.target.value || "";

    setForm((prev) => ({
      ...prev,
      date: isoDate,
    }));

    setDateInput(formatDateToInputText(isoDate));
    clearError("date");

    window.setTimeout(() => {
      forceCloseNativeDatePicker();
    }, 0);
  };

  const openNativeDatePicker = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const picker = datePickerRef.current;

    if (!picker) return;

    picker.focus();

    if (typeof picker.showPicker === "function") {
      picker.showPicker();
      return;
    }

    picker.click();
  };

  const handlePlaceSelect = (selectedOption) => {
    if (!selectedOption) {
      setForm((prev) => ({
        ...prev,
        placeId: null,
        location: "",
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
      }));

      clearError("location");
      return;
    }

    if (selectedOption.__isNew__) {
      setForm((prev) => ({
        ...prev,
        placeId: null,
        location: selectedOption.label || selectedOption.value || "",
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
      }));
    } else {
      const p = selectedOption.fullData || selectedOption;

      setForm((prev) => ({
        ...prev,
        placeId: p.id ?? selectedOption.value ?? null,
        location: getPlaceName(p) || selectedOption.label || "",
        address: p.address || p.streetAddress || p.street_address || "",
        postalCode: getPlacePostalCode(p),
        city: p.city || p.locality || "",
        country: p.country || "",
        gMapsLink: getPlaceGMapsLink(p),
        coordinates: p.coordinates || p.coords || p.latLng || p.lat_lng || "",
        contactPhone: getPlaceContactPhone(p),
      }));
    }

    clearError("location");
    clearError("address");
    clearError("postalCode");
    clearError("city");
    clearError("country");
    clearError("gMapsLink");
    clearError("coordinates");
    clearError("contactPhone");
  };

  const handlePointPurposeSelect = (selectedOption) => {
    setForm((prev) => ({
      ...prev,
      strategicPointPurpose: selectedOption?.value || "GENERAL",
    }));

    clearError("strategicPointPurpose");
  };

  const handleClearForm = () => {
    setForm({
      location: "",
      strategicPointPurpose: "GENERAL",
      hasTrailerSwap: false,
      date: defaultDate || "",
      hour: "",
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",
      placeId: null,
    });

    setDateInput(formatDateToInputText(defaultDate || ""));
    setHourInput("");

    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const cleanLocation = String(form.location || "").trim();

    if (!cleanLocation) {
      setErrors({
        location: true,
      });
      return;
    }

    setErrors({});

    onSubmit({
      ...form,
      placeId: form.placeId ?? null,
      location: cleanLocation,
      date: String(form.date || "").trim() || null,
      hour: String(form.hour || "").trim() || null,
      address: String(form.address || "").trim() || null,
      postalCode: String(form.postalCode || "").trim() || null,
      city: String(form.city || "").trim() || null,
      country: String(form.country || "").trim() || null,
      gMapsLink: String(form.gMapsLink || "").trim() || null,
      coordinates: String(form.coordinates || "").trim() || null,
      contactPhone: String(form.contactPhone || "").trim() || null,
      strategicPointPurpose:
        String(form.strategicPointPurpose || "GENERAL").trim().toUpperCase() === "FUEL"
          ? "FUEL"
          : "GENERAL",
      hasTrailerSwap: !!form.hasTrailerSwap,
    });
  };

  const modalRef = useRef(null);
  const datePickerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      const isInsideModal = modalRef.current?.contains(event.target);

      const isInsideSelect =
        event.target.closest(".sp__control") ||
        event.target.closest(".sp__menu") ||
        event.target.closest(".sp__option") ||
        event.target.closest(".sp__menu-list") ||
        event.target.closest(".sp-select") ||
        event.target.closest(".sp");

      const isInsideDateField =
        event.target.closest(".sp-date-shell") ||
        event.target.closest(".sp-hidden-date-picker");

      if (!isInsideDateField && !isInsideSelect) {
        forceCloseNativeDatePicker();
      }

      if (!isInsideModal && !isInsideSelect) {
        onClose();
      }
    }

    if (isOpen) document.addEventListener("pointerdown", handleClickOutside);

    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="strategic-point-modal-overlay">
      <div className="strategic-point-modal" ref={modalRef}>
        <form onSubmit={handleSubmit} className="sp-modal-form">
          <div className="strategic-point-modal-body">
            <div className="sp-modern-section">
              <div className="sp-section-header">
                <FaUsers className="sp-section-icon" />
                <span>{t("sections.mainInfo", "Informação principal")}</span>
              </div>

              <div className="sp-modern-grid sp-modern-grid-2">
                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.nameLabel", "Nome / Etiqueta")} <span className="sp-required">*</span>
                  </span>

                  <div className={`sp-input-shell sp-select-shell ${errors.location ? "input-error" : ""}`}>
                    <FaMapMarkerAlt className="sp-field-icon" />

                    <CreatableSelect
                      isClearable
                      options={placeOptions}
                      placeholder={t("placeholders.selectOrTypePlace", "Selecionar ou escrever um local")}
                      value={selectedPlaceValue}
                      getOptionValue={(option) => String(option.value)}
                      getOptionLabel={(option) => option.label || ""}
                      onChange={handlePlaceSelect}
                      className="sp-select"
                      classNamePrefix="sp"
                      styles={blueSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  {errors.location && <span className="error">{t("errors.locationRequired")}</span>}
                </label>

                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.contactPhone", "Telefone de Contacto")}
                  </span>

                  <div className={`sp-input-shell ${errors.contactPhone ? "input-error" : ""}`}>
                    <FaPhoneAlt className="sp-field-icon" />

                    <input
                      name="contactPhone"
                      value={form.contactPhone}
                      onChange={handleChange}
                      placeholder={t("placeholders.contactPhone", "Telefone de contacto")}
                    />
                  </div>

                  {errors.contactPhone && <span className="error">{t("errors.required")}</span>}
                </label>
              </div>

              <label className="sp-modern-field">
                <span className="sp-field-label">
                  {t("fields.address", "Morada")}
                </span>

                <div className={`sp-input-shell ${errors.address ? "input-error" : ""}`}>
                  <FaMapMarkerAlt className="sp-field-icon" />

                  <input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder={t("placeholders.address", "Morada completa")}
                  />
                </div>

                {errors.address && <span className="error">{t("errors.required")}</span>}
              </label>
            </div>

            <div className="sp-modern-section">
              <div className="sp-section-header">
                <FaCalendarAlt className="sp-section-icon" />
                <span>{t("sections.schedule", "Agendamento")}</span>
              </div>

              <div className="sp-modern-grid sp-modern-grid-3 sp-schedule-grid">
                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.date", "Data")}
                  </span>

                  <div
                    className={`sp-input-shell sp-date-shell ${errors.date ? "input-error" : ""}`}
                    onClick={(e) => {
                      if (e.target.closest("input")) return;
                      openNativeDatePicker(e);
                    }}
                  >
                    <input
                      type="text"
                      name="date"
                      value={dateInput}
                      onChange={handleDateChange}
                      id="strategic-date"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onClick={(e) => e.stopPropagation()}
                    />

                    <input
                      key={datePickerKey}
                      ref={datePickerRef}
                      type="date"
                      value={form.date || ""}
                      onChange={handleNativeDateChange}
                      className="sp-hidden-date-picker"
                      tabIndex={-1}
                      aria-hidden="true"
                    />

                    <FaCalendarAlt
                      className="sp-field-action-icon clickable"
                      onClick={openNativeDatePicker}
                    />
                  </div>

                  {errors.date && <span className="error">{t("errors.required")}</span>}
                </label>

                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.hour", "Hora")}
                  </span>

                  <div className={`sp-input-shell sp-hour-shell ${errors.hour ? "input-error" : ""}`}>
                    <input
                      type="text"
                      name="hour"
                      value={hourInput}
                      onChange={handleHourChange}
                      id="strategic-hour"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="HH:MM"
                      maxLength={5}
                    />

                    <FaClock
                      className="sp-field-action-icon clickable"
                      onClick={(e) => {
                        e.preventDefault();

                        const input = document.getElementById("strategic-hour");
                        if (input) input.focus();
                      }}
                    />
                  </div>

                  {errors.hour && <span className="error">{t("errors.required")}</span>}
                </label>

                <label className="sp-modern-field">
                  <span className="sp-field-label sp-label-with-info">
                    {t("fields.pointPurpose", "Tipo de ponto")}
                  </span>

                  <div className={`sp-input-shell sp-select-shell sp-purpose-select-shell ${errors.strategicPointPurpose ? "input-error" : ""}`}>
                    <Select
                      options={pointPurposeOptions}
                      value={selectedPointPurposeValue}
                      onChange={handlePointPurposeSelect}
                      className="sp-select"
                      classNamePrefix="sp"
                      styles={blueSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isSearchable={false}
                    />
                  </div>

                  {errors.strategicPointPurpose && (
                    <span className="error">{t("errors.required")}</span>
                  )}
                </label>
              </div>
            </div>

            <div className="sp-modern-section sp-trailer-swap-section">
              <div className="sp-section-header">
                <FaExchangeAlt className="sp-section-icon" />
                <span>{t("sections.trailerSwap", "Reboque")}</span>
              </div>

              <label className="sp-trailer-swap-toggle">
                <input
                  type="checkbox"
                  name="hasTrailerSwap"
                  checked={!!form.hasTrailerSwap}
                  onChange={handleChange}
                />

                <span className="sp-trailer-swap-control" aria-hidden="true">
                  <span className="sp-trailer-swap-knob" />
                </span>

                <span className="sp-trailer-swap-copy">
                  <strong>{t("fields.hasTrailerSwap", "Troca de reboque neste ponto")}</strong>
                  <small>
                    {form.hasTrailerSwap
                      ? t(
                          "hints.hasTrailerSwapOn",
                          "Ativo: no ponto estratégico vão aparecer dois campos — reboque à chegada e reboque à saída."
                        )
                      : t(
                          "hints.hasTrailerSwapOff",
                          "Inativo: no ponto estratégico aparece apenas um campo de reboque."
                        )}
                  </small>
                </span>
              </label>
            </div>

            <div className="sp-modern-section">
              <div className="sp-section-header">
                <FaGlobeEurope className="sp-section-icon" />
                <span>{t("sections.location", "Localização")}</span>
              </div>

              <div className="sp-modern-grid sp-modern-grid-3">
                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.postalCode", "Código Postal")}
                  </span>

                  <div className={`sp-input-shell ${errors.postalCode ? "input-error" : ""}`}>
                    <FaMapMarkerAlt className="sp-field-icon" />

                    <input
                      name="postalCode"
                      value={form.postalCode}
                      onChange={handleChange}
                      placeholder={t("placeholders.postalCode", "Código postal")}
                    />
                  </div>

                  {errors.postalCode && <span className="error">{t("errors.required")}</span>}
                </label>

                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.city", "Cidade")}
                  </span>

                  <div className={`sp-input-shell ${errors.city ? "input-error" : ""}`}>
                    <FaCity className="sp-field-icon" />

                    <input
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      placeholder={t("placeholders.city", "Cidade")}
                    />
                  </div>

                  {errors.city && <span className="error">{t("errors.required")}</span>}
                </label>

                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.country", "País")}
                  </span>

                  <div className={`sp-input-shell ${errors.country ? "input-error" : ""}`}>
                    <FaFlag className="sp-field-icon" />

                    <input
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                      placeholder={t("placeholders.country", "País")}
                    />
                  </div>

                  {errors.country && <span className="error">{t("errors.required")}</span>}
                </label>
              </div>

              <div className="sp-modern-grid sp-modern-grid-2">
                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.gMapsLink", "Link do Google Maps")}
                  </span>

                  <div className={`sp-input-shell ${errors.gMapsLink ? "input-error" : ""}`}>
                    <FaLink className="sp-field-icon" />

                    <input
                      name="gMapsLink"
                      value={form.gMapsLink}
                      onChange={handleChange}
                      placeholder={t("placeholders.gMapsLink", "https://maps.google.com/...")}
                    />
                  </div>

                  <span className="sp-field-hint">
                    {t("hints.gMapsLink", "Cole o link do Google Maps para este local.")}
                  </span>

                  {errors.gMapsLink && <span className="error">{t("errors.required")}</span>}
                </label>

                <label className="sp-modern-field">
                  <span className="sp-field-label">
                    {t("fields.coordinates", "Coordenadas (Lat, Lng)")}
                  </span>

                  <div className={`sp-input-shell ${errors.coordinates ? "input-error" : ""}`}>
                    <FaCrosshairs className="sp-field-icon" />

                    <input
                      name="coordinates"
                      value={form.coordinates}
                      onChange={handleChange}
                      placeholder={t("placeholders.coordinates", "ex.: 41.1234, -8.1234")}
                    />
                  </div>

                  <span className="sp-field-hint">
                    {t("hints.coordinates", "Formato: latitude, longitude (ex.: 41.1234, -8.1234).")}
                  </span>

                  {errors.coordinates && <span className="error">{t("errors.required")}</span>}
                </label>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleClearForm} className="sp-clear-btn">
              <FaTrashAlt />
              {t("buttons.clear", "Limpar")}
            </button>

            <div className="sp-actions-right">
              <button type="button" onClick={onClose} className="cancel-btn">
                {t("buttons.cancel", "Cancelar")}
              </button>

              <button type="submit" className="sp-submit-btn">
                <FaMagic />
                {mode === "edit"
                  ? t("buttons.save", "Guardar")
                  : t("buttons.add", "Adicionar ponto")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default StrategicPointModal;